import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const IntelligentAutomation = () => {
  return (
    <>
      <SEO 
        title="Intelligent Automation - AI-Powered Process Automation Solutions"
        description="Transform your business operations with intelligent automation combining AI, machine learning, and RPA. Reduce costs by up to 70% while improving accuracy and efficiency."
        keywords="intelligent automation, AI automation, RPA, business process automation, workflow automation, machine learning automation, enterprise automation"
      />

      {/* Hero Section */}
      <section className="hero-section relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blush-light/20 to-transparent"></div>
        <div className="page-container relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-4 px-4 py-2 bg-primary-red/10 rounded-full">
              <span className="text-primary-red font-semibold text-sm">AI Hub</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-fade-in">
              <span className="text-gradient">Intelligent Automation</span> for Enterprise Excellence
            </h1>
            <p className="text-xl text-titanium leading-relaxed mb-8 animate-slide-up">
              Combine AI, machine learning, and robotic process automation to create intelligent workflows that adapt, learn, and optimize your business operations automatically.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <Link to="/contact" className="btn-primary">
                Start Your Automation Journey
              </Link>
              <Link to="/ai-hub" className="btn-secondary">
                Explore AI Hub
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Why Intelligent Automation?</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Go beyond traditional automation with AI-powered solutions that learn, adapt, and continuously improve
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
                title: '70% Cost Reduction',
                description: 'Dramatically reduce operational costs through automated processes and optimized resource allocation'
              },
              {
                icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
                title: '99.9% Accuracy',
                description: 'Eliminate human error with AI-powered validation and quality control mechanisms'
              },
              {
                icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
                title: '24/7 Operations',
                description: 'Enable round-the-clock processing with automated workflows that never sleep'
              },
              {
                icon: 'M13 10V3L4 14h7v7l9-11h-7z',
                title: '10x Faster Processing',
                description: 'Accelerate business processes with intelligent automation that scales instantly'
              }
            ].map((benefit, index) => (
              <div key={index} className="card text-center group hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-red to-coral-mist rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={benefit.icon} />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-3 text-charcoal">{benefit.title}</h3>
                <p className="text-titanium">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What is Intelligent Automation */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal">
                What is <span className="text-gradient">Intelligent Automation</span>?
              </h2>
              <p className="text-titanium mb-6">
                Intelligent Automation (IA) represents the convergence of Robotic Process Automation (RPA), Artificial Intelligence (AI), and Machine Learning (ML) to create adaptive, self-improving systems that transform how businesses operate.
              </p>
              <p className="text-titanium mb-6">
                Unlike traditional automation that simply follows predefined rules, intelligent automation can understand context, make decisions, learn from patterns, and continuously optimize processes without human intervention.
              </p>
              <div className="space-y-4">
                {[
                  'AI-powered decision making and cognitive processing',
                  'Natural language processing for unstructured data',
                  'Computer vision for document and image recognition',
                  'Predictive analytics for proactive optimization',
                  'Self-learning algorithms that improve over time',
                  'Seamless integration with existing enterprise systems'
                ].map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <svg className="w-6 h-6 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-titanium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="bg-white rounded-2xl p-8 shadow-xl">
                <div className="space-y-6">
                  <div className="border-l-4 border-primary-red pl-6">
                    <h4 className="font-bold text-charcoal mb-2">Traditional RPA</h4>
                    <p className="text-sm text-titanium">Rule-based, repetitive tasks only</p>
                  </div>
                  <div className="border-l-4 border-coral-mist pl-6">
                    <h4 className="font-bold text-charcoal mb-2">+ Artificial Intelligence</h4>
                    <p className="text-sm text-titanium">Cognitive capabilities & decision making</p>
                  </div>
                  <div className="border-l-4 border-deep-garnet pl-6">
                    <h4 className="font-bold text-charcoal mb-2">+ Machine Learning</h4>
                    <p className="text-sm text-titanium">Self-learning & continuous improvement</p>
                  </div>
                  <div className="bg-gradient-to-r from-primary-red/10 to-coral-mist/10 p-6 rounded-xl mt-4">
                    <h4 className="font-bold text-primary-red mb-2 text-lg">= Intelligent Automation</h4>
                    <p className="text-sm text-charcoal">End-to-end autonomous business processes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Core Capabilities</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Comprehensive automation solutions powered by advanced AI technologies
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Intelligent Document Processing',
                description: 'Extract, classify, and process documents using advanced OCR, NLP, and machine learning for automated data extraction.',
                features: ['Invoice processing', 'Contract analysis', 'Form extraction', 'Data validation']
              },
              {
                title: 'Workflow Orchestration',
                description: 'Design and manage complex, multi-system workflows with AI-powered routing and decision-making capabilities.',
                features: ['Process mining', 'Smart routing', 'Exception handling', 'SLA monitoring']
              },
              {
                title: 'Cognitive Automation',
                description: 'Enable systems to understand, reason, and make decisions using natural language processing and machine learning.',
                features: ['Email categorization', 'Sentiment analysis', 'Intent recognition', 'Response generation']
              },
              {
                title: 'Predictive Process Analytics',
                description: 'Leverage AI to predict bottlenecks, optimize resource allocation, and prevent issues before they occur.',
                features: ['Performance forecasting', 'Anomaly detection', 'Capacity planning', 'Risk assessment']
              },
              {
                title: 'API & System Integration',
                description: 'Seamlessly connect disparate systems and automate data flow across your entire technology ecosystem.',
                features: ['Legacy system integration', 'Cloud connectivity', 'Real-time sync', 'Data transformation']
              },
              {
                title: 'Intelligent Chatbots & Virtual Assistants',
                description: 'Deploy AI-powered conversational interfaces for customer service, internal support, and process automation.',
                features: ['24/7 availability', 'Multi-language support', 'Context awareness', 'Human handoff']
              }
            ].map((capability, index) => (
              <div key={index} className="card group hover:shadow-xl transition-all duration-300">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-bold text-charcoal group-hover:text-primary-red transition-colors">
                    {capability.title}
                  </h3>
                  <svg className="w-6 h-6 text-primary-red flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
                <p className="text-titanium mb-4">{capability.description}</p>
                <div className="space-y-2">
                  {capability.features.map((feature, fIndex) => (
                    <div key={fIndex} className="flex items-center text-sm">
                      <div className="w-1.5 h-1.5 bg-primary-red rounded-full mr-2"></div>
                      <span className="text-graphite">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases by Industry */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-alabaster to-pearl-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Industry Use Cases</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Real-world applications transforming businesses across industries
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            {[
              {
                industry: 'Financial Services',
                icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
                useCases: [
                  'Automated loan processing and approval',
                  'Fraud detection and prevention',
                  'Customer onboarding and KYC verification',
                  'Regulatory compliance reporting'
                ]
              },
              {
                industry: 'Healthcare',
                icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
                useCases: [
                  'Medical claims processing and adjudication',
                  'Patient appointment scheduling',
                  'Electronic health record management',
                  'Insurance eligibility verification'
                ]
              },
              {
                industry: 'Manufacturing',
                icon: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z',
                useCases: [
                  'Supply chain optimization and forecasting',
                  'Quality control and inspection',
                  'Inventory management automation',
                  'Production scheduling and planning'
                ]
              },
              {
                industry: 'Retail & E-commerce',
                icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z',
                useCases: [
                  'Order processing and fulfillment',
                  'Customer service automation',
                  'Dynamic pricing optimization',
                  'Returns and refunds management'
                ]
              },
              {
                industry: 'Human Resources',
                icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
                useCases: [
                  'Resume screening and candidate matching',
                  'Employee onboarding workflows',
                  'Payroll processing automation',
                  'Benefits administration'
                ]
              },
              {
                industry: 'Customer Service',
                icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
                useCases: [
                  'Intelligent ticket routing and prioritization',
                  'Automated response generation',
                  'Sentiment analysis and escalation',
                  'Customer feedback processing'
                ]
              }
            ].map((industry, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex items-center mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-primary-red to-coral-mist rounded-xl flex items-center justify-center mr-4">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={industry.icon} />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-charcoal">{industry.industry}</h3>
                </div>
                <ul className="space-y-3">
                  {industry.useCases.map((useCase, uIndex) => (
                    <li key={uIndex} className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-titanium">{useCase}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Our Implementation Process</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              A proven methodology to ensure successful automation deployment
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Discovery & Assessment',
                description: 'Analyze current processes, identify automation opportunities, and prioritize based on ROI potential'
              },
              {
                step: '02',
                title: 'Design & Development',
                description: 'Create automation blueprints, develop intelligent workflows, and integrate AI capabilities'
              },
              {
                step: '03',
                title: 'Testing & Validation',
                description: 'Rigorous testing in staging environments, user acceptance testing, and performance optimization'
              },
              {
                step: '04',
                title: 'Deployment & Support',
                description: 'Phased rollout, team training, continuous monitoring, and ongoing optimization'
              }
            ].map((phase, index) => (
              <div key={index} className="relative">
                <div className="card hover:shadow-xl transition-all duration-300">
                  <div className="text-5xl font-bold text-primary-red/20 mb-4">{phase.step}</div>
                  <h3 className="text-xl font-bold text-charcoal mb-3">{phase.title}</h3>
                  <p className="text-titanium">{phase.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <svg className="w-8 h-8 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
                  Calculate Your <span className="text-gradient">ROI</span>
                </h2>
                <p className="text-titanium">
                  See the potential impact of intelligent automation on your business
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="text-center p-6 bg-gradient-to-br from-primary-red/5 to-coral-mist/5 rounded-xl">
                  <div className="text-4xl font-bold text-primary-red mb-2">$2.4M</div>
                  <div className="text-sm text-titanium">Average Annual Savings</div>
                </div>
                <div className="text-center p-6 bg-gradient-to-br from-primary-red/5 to-coral-mist/5 rounded-xl">
                  <div className="text-4xl font-bold text-primary-red mb-2">6-12</div>
                  <div className="text-sm text-titanium">Months to Full ROI</div>
                </div>
                <div className="text-center p-6 bg-gradient-to-br from-primary-red/5 to-coral-mist/5 rounded-xl">
                  <div className="text-4xl font-bold text-primary-red mb-2">400%</div>
                  <div className="text-sm text-titanium">Average ROI in 3 Years</div>
                </div>
              </div>
              <div className="text-center">
                <Link to="/contact" className="btn-primary inline-block">
                  Get Your Custom ROI Analysis
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies & Tools */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Technologies & Platforms</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Leveraging best-in-class automation technologies and AI platforms
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              'UiPath', 'Blue Prism', 'Automation Anywhere', 'Microsoft Power Automate',
              'TensorFlow', 'PyTorch', 'AWS AI Services', 'Azure Cognitive Services',
              'Google Cloud AI', 'IBM Watson', 'OpenAI GPT', 'Anthropic Claude'
            ].map((tech, index) => (
              <div key={index} className="card text-center hover:shadow-lg transition-all duration-300">
                <p className="font-semibold text-charcoal">{tech}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Preview */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-charcoal to-graphite text-white">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block px-4 py-2 bg-white/10 rounded-full mb-4">
                <span className="text-coral-mist font-semibold text-sm">Success Story</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Global Bank Achieves 85% Processing Time Reduction
              </h2>
              <p className="text-xl text-pearl-white/80">
                How intelligent automation transformed loan processing operations
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-coral-mist mb-2">85%</div>
                <div className="text-sm text-pearl-white/80">Faster Processing</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-coral-mist mb-2">$3.2M</div>
                <div className="text-sm text-pearl-white/80">Annual Cost Savings</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-coral-mist mb-2">99.7%</div>
                <div className="text-sm text-pearl-white/80">Accuracy Rate</div>
              </div>
            </div>
            <div className="text-center">
              <Link to="/contact" className="inline-block bg-white text-charcoal px-8 py-4 rounded-lg font-semibold hover:bg-pearl-white transition-all duration-300">
                Read Full Case Study
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-primary-red text-white">
        <div className="page-container text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Business Operations?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            Start your intelligent automation journey with a free consultation and ROI assessment from our expert team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg">
              Schedule Free Consultation
            </Link>
            <Link to="/ai-hub" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
              Explore More AI Solutions
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default IntelligentAutomation;